import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

import { runService } from "@/services/runService";
import { Field } from "@/components/profile/FormControls";
import { RelatedEntityPicker } from "@/components/run/RelatedEntityPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none__";

/** Rename / move / relink a document's metadata. */
export const DocumentEditDialog = ({ open, onOpenChange, document, folders = [], onSaved }) => {
  const [name, setName] = useState("");
  const [folderId, setFolderId] = useState(NONE);
  const [related, setRelated] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && document) {
      setName(document.file_name || "");
      setFolderId(document.folder?.id || NONE);
      setRelated(document.related?.id ? document.related : null);
    }
  }, [open, document]);

  const save = async () => {
    setSaving(true);
    try {
      await runService.updateDocument(document.id, {
        file_name: name,
        folder_id: folderId === NONE ? null : folderId,
        related_entity_type: related?.type || null,
        related_entity_id: related?.id || null,
      });
      toast.success("Document updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(err.message || "Could not update"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="document-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-accent" /> Edit Document</DialogTitle>
          <DialogDescription>Rename, move to a folder, or link this file to a record.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="File name" htmlFor="doc-name"><Input id="doc-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="doc-edit-name" /></Field>
          <Field label="Folder" htmlFor="doc-edit-folder">
            <Select value={folderId} onValueChange={setFolderId}>
              <SelectTrigger id="doc-edit-folder" data-testid="doc-edit-folder"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No folder</SelectItem>
                {folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Linked to"><RelatedEntityPicker value={related} onChange={setRelated} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="doc-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="doc-edit-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
